import React, { useEffect, useRef, useState } from "react";
import videojs from "video.js";
import "video.js/dist/video-js.css";
import "videojs-youtube";
import "videojs-hls-quality-selector";
import { isMobile } from "@/lib/utils";

let currentlyPlaying: videojs.Player | null = null;

const isYouTubeUrl = (url: string) =>
  /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.?be)\//.test(url);

interface VideoPlayerProps {
  videoUrl: string;
  videoType?: string;
  thumbnailUrl?: string;
  aspectRatio?: string;
}

export const VideoPlayer: React.FC<VideoPlayerProps> = ({
  videoUrl,
  videoType,
  thumbnailUrl,
  aspectRatio = "16:9", // Default aspect ratio
}) => {
  const videoRef = useRef<HTMLDivElement | null>(null);
  const playerRef = useRef<any>(null);
  const [showSeekOverlay, setShowSeekOverlay] = useState<
    "forward" | "backward" | null
  >(null);
  const [isMobileView] = useState(isMobile());

  useEffect(() => {
    if (!videoRef.current) return;

    const isYouTube = isYouTubeUrl(videoUrl);

    const videoJsOptions: any = {
      autoplay: false,
      controls: true,
      responsive: true,
      fluid: true,
      loop: true,
      poster: thumbnailUrl,
      playbackRates: [0.5, 1, 1.5, 2],
      ...(isMobileView
        ? {
            aspectRatio: "1:1", // Use 1:1 aspect ratio for mobile devices
          }
        : {
            aspectRatio,
          }),
    };

    const getStandardYouTubeUrl = (url: string) => {
      const shortsMatch = url.match(/youtube\.com\/shorts\/([\w-]+)/);
      if (shortsMatch) {
        const videoId = shortsMatch[1];
        return `https://www.youtube.com/watch?v=${videoId}`;
      }
      return url;
    };

    if (isYouTube) {
      videoJsOptions.sources = [
        { type: "video/youtube", src: getStandardYouTubeUrl(videoUrl) },
      ];
      videoJsOptions.techOrder = ["youtube"];
    } else {
      let typeToUse = videoType || "video/mp4";

      if (videoUrl.includes(".m3u8")) {
        typeToUse = "application/x-mpegURL";
      }

      videoJsOptions.sources = [{ src: videoUrl, type: typeToUse }];
    }

    if (!playerRef.current) {
      const videoElement = document.createElement("video-js");
      videoElement.classList.add("vjs-big-play-centered");
      videoRef.current.appendChild(videoElement);

      // @ts-ignore
      playerRef.current = videojs(
        videoElement,
        videoJsOptions,
        function (this: videojs.Player) {
          // @ts-ignore
          this.on("play", () => {
            if (currentlyPlaying && currentlyPlaying !== this) {
              // @ts-ignore
              currentlyPlaying.pause(); // When this video starts playing, pause any other video that might be playing
            }
            currentlyPlaying = this;
          });

          // @ts-ignore
          this.on("pause", () => {
            if (currentlyPlaying === this) {
              currentlyPlaying = null; // When this video is paused, clear it from currentlyPlaying variable
            }
          });

          if (
            !isYouTube &&
            videoUrl.includes(".m3u8") &&
            typeof (this as any).hlsQualitySelector === "function"
          ) {
            (this as any).hlsQualitySelector({
              displayCurrentQuality: true,
            });
          }
        }
      );

      let lastTap = 0;
      let tapTimer: NodeJS.Timeout | null = null;

      playerRef.current.on("touchstart", (event: any) => {
        event.preventDefault();

        const time = Date.now();
        const isDoubleTap = time - lastTap < 300;
        lastTap = time;

        if (isDoubleTap) {
          if (tapTimer) {
            clearTimeout(tapTimer);
            tapTimer = null;
          }

          const rect = playerRef.current.el().getBoundingClientRect();
          const tapX = event.touches[0].clientX - rect.left;

          if (tapX > rect.width / 2) {
            playerRef.current.currentTime(playerRef.current.currentTime() + 10);
            setShowSeekOverlay("forward");
          } else {
            playerRef.current.currentTime(playerRef.current.currentTime() - 10);
            setShowSeekOverlay("backward");
          }
          setTimeout(() => setShowSeekOverlay(null), 500);
        } else {
          tapTimer = setTimeout(() => {
            const rect = playerRef.current.el().getBoundingClientRect();
            const tapX = event.touches[0].clientX - rect.left;
            if (tapX > rect.width * 0.25 && tapX < rect.width * 0.75) {
              if (playerRef.current.paused()) {
                playerRef.current.play();
              } else {
                playerRef.current.pause();
              }
            }
          }, 300);
        }
      });
    } else {
      let typeToUse = isYouTube
        ? "video/youtube"
        : videoUrl.includes(".m3u8")
          ? "application/x-mpegURL"
          : videoType || "video/mp4";
      playerRef.current.src({ type: typeToUse, src: videoUrl });
    }
  }, [videoUrl, videoType, thumbnailUrl, aspectRatio, isMobileView]);

  useEffect(() => {
    const player = playerRef.current;
    if (!player) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") {
        player.currentTime(player.currentTime() + 10);
        setShowSeekOverlay("forward");
        setTimeout(() => setShowSeekOverlay(null), 500);
      } else if (e.key === "ArrowLeft") {
        player.currentTime(player.currentTime() - 10);
        setShowSeekOverlay("backward");
        setTimeout(() => setShowSeekOverlay(null), 500);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  useEffect(() => {
    const player = playerRef.current;
    if (!player) return;

    // Intersection Observer to detect visibility
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
          if (currentlyPlaying && currentlyPlaying !== player) {
            // @ts-ignore
            currentlyPlaying.pause();
          }
          player.play().catch(() => {});
          currentlyPlaying = player;
        } else {
          if (currentlyPlaying === player) {
            player.pause();
            currentlyPlaying = null;
          }
        }
      },
      { threshold: [0.5] }
    );

    if (videoRef.current) {
      observer.observe(videoRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [playerRef.current, videoRef.current]);

  useEffect(() => {
    const player = playerRef.current;
    return () => {
      if (player && !player.isDisposed()) {
        if (currentlyPlaying === player) {
          currentlyPlaying = null; //if user move away reset the player to null
        }
        player.dispose(); //then dispose it
        playerRef.current = null;
      }
    };
  }, []);

  return (
    <div data-vjs-player style={{ position: "relative" }}>
      <div ref={videoRef} />
      {showSeekOverlay && (
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: showSeekOverlay === "forward" ? "75%" : "25%",
            transform: "translate(-50%, -50%)",
            color: "white",
            fontSize: "2rem",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            borderRadius: "50%",
            padding: "20px",
            zIndex: 999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "80px",
            height: "80px",
          }}
        >
          {showSeekOverlay === "forward" ? ">>" : "<<"}
        </div>
      )}
    </div>
  );
};

export default VideoPlayer;
